import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { EditableModal } from "../components/editableModal";
import { INPUT_TYPE } from "../constants";
import AppContext from "../context/app-context";
import CreditCardsService from "../services/CreditCardsService";
import { getCreditCardBenefitsConfig } from "../utils/AmexContants";

import { Container, Row, Col, Card, Button, Badge, ListGroup, Modal } from "react-bootstrap";
import { FaCreditCard, FaPlusCircle, FaInfoCircle, FaCheckCircle } from "react-icons/fa";
import { MdSignalCellularAlt } from "react-icons/md";
import { toast } from "react-toastify";

export default function CreditCardPage() {
  const { t } = useTranslation();
  const { userProfile, setLoading } = useContext(AppContext);
  const [creditCards, setCreditCards] = useState([]);
  const [selectedBenefit, setSelectedBenefit] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const { userId } = userProfile || {};
  const loadData = () => {
    setLoading(true);
    CreditCardsService.getCreditCards({
      callback: ({ data }) => processCards(data),
    });
  };
  useEffect(() => {
    loadData();
  }, []);

  const processCards = (data) => {
    console.log(data);
    const { cards = [], benefitsExpiringSoon: { Items = [] } = {} } = data || {};
    const processedCards = cards.map((card) => {
      const benefits = Items.filter((item) => item.userCardId === card.userCardId).map((item) => {
        const daysRemaining = Math.floor((new Date(item.periodEnd) - new Date()) / (1000 * 60 * 60 * 24));
        const isUsed = item.status === "USED";

        let statusColor;
        if (isUsed) statusColor = "#6c757d";
        else if (daysRemaining <= 5) statusColor = "#dc3545";
        else if (daysRemaining <= 15) statusColor = "#ffc107";
        else statusColor = "#28a745";

        return {
          ...item,
          daysRemaining,
          statusColor,
          config: getCreditCardBenefitsConfig(card.cardId).find((c) => c.benefitId === item.benefitId),
        };
      });
      // .sort((a, b) => (a.status === "USED" ? 1 : -1));

      return { ...card, benefits };
    });
    setLoading(false);
    setCreditCards(processedCards);
  };

  const handleMarkUsed = (benefit) => {
    setLoading(true);
    CreditCardsService.updateCreditCard({
      payload: {
        userCardId: benefit.userCardId,
        benefit: { benefitId: benefit.benefitId, periodStart: benefit.periodStart },
      },
      callback: ({ data }) => {
        loadData();
      },
    });
  };
  console.log(creditCards);
  return (
    <Container className="py-4">
      {/* Header Section */}
      <Row className="mb-4 align-items-center">
        <Col>
          <h1 className="mb-0">
            <FaCreditCard className="me-2 text-primary" />
            {t("CreditCardPage.title")}
          </h1>
        </Col>
        <Col xs="auto">
          <EditableModal
            title={t("CreditCardPage.addCreditCard")}
            show={showAddModal}
            onHide={() => setShowAddModal(false)}
            configs={[
              {
                fieldName: "cardNickName",
                label: "cardNickName",
                inputType: INPUT_TYPE.TEXT,
                required: true,
              },
              {
                fieldName: "last4Digits",
                label: "Last 4 Digits",
                inputType: INPUT_TYPE.NUMBER,
                required: true,
              },
              {
                fieldName: "applyDate",
                label: "applyDate",
                inputType: INPUT_TYPE.DATE,
              },
              {
                fieldName: "cardId",
                label: "cardId",
                inputType: INPUT_TYPE.SELECT,
                enumValues: [
                  { name: "AMEX PLATINUM", value: "AMEXPLATINUM" },
                  { name: "AMEX GOLD", value: "AMEXGOLD" },
                  // { name: "AMEX BLUE CASH PREFERRED", value: "AMEX_BLUE_CASH_PREFERRED" },
                ],
              },
            ]}
            actionLabel={t("CreditCardPage.addCard")}
            actionOnClick={(data) => {
              if (!userId) {
                toast.error("Please login first.");
                return;
              }
              setLoading(true);
              CreditCardsService.createCreditCard({
                payload: { userId: userId, ...data },
                callback: ({ data }) => {
                  loadData();
                },
              });
            }}
          />
        </Col>
      </Row>

      {/* Cards Grid */}
      <Row xs={1} md={2} lg={3} className="g-4">
        {creditCards.map((card) => (
          <Col key={card.userCardId}>
            <Card className="h-100 shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h5 className="mb-0">{card.cardNickName}</h5>
                    <small className="text-muted">{card.cardId}</small>
                  </div>
                  <MdSignalCellularAlt size={24} className="text-primary" />
                </div>

                <ListGroup variant="flush">
                  <ListGroup.Item className="d-flex justify-content-between">
                    <span>{t("CreditCardPage.addedDate")}</span>
                    <span>{new Date(card.createdDate).toLocaleDateString()}</span>
                  </ListGroup.Item>

                  <ListGroup.Item>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong>{t("CreditCardPage.activeBenefits")}</strong>
                      <Badge bg="primary">{card.benefits.length}</Badge>
                    </div>

                    <div className="d-flex flex-column gap-2">
                      {card.benefits.map((benefit) => (
                        <div
                          key={benefit.SK}
                          className="d-flex justify-content-between align-items-start p-2 rounded-3 position-relative"
                          role="button"
                          tabIndex={0}
                          onClick={() => setSelectedBenefit(benefit)}
                          style={{
                            border: `2px solid ${benefit.statusColor}`,
                            backgroundColor: benefit.status === "USED" ? "#f8f9fa" : "white",
                            cursor: "pointer",
                          }}
                        >
                          <div
                            className="position-absolute start-0 top-0 h-100"
                            style={{
                              width: "4px",
                              backgroundColor: benefit.statusColor,
                            }}
                          />
                          {/* Left Content */}
                          <div className="flex-grow-1 pe-2">
                            <div className="fw-medium" style={{ color: benefit.status === "USED" ? "#6c757d" : "inherit" }}>
                              {benefit.config?.label}
                            </div>
                            {benefit.status !== "USED" && (
                              <small className="text-muted">
                                {t("CreditCardPage.expiresIn")} {benefit.daysRemaining} {t("CreditCardPage.days")}
                              </small>
                            )}
                          </div>

                          {/* Right Actions */}
                          <div className="d-flex align-items-center gap-2">
                            {benefit.status !== "USED" && (
                              <div
                                role="button"
                                tabIndex={0}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMarkUsed(benefit);
                                }}
                                className="p-1"
                                style={{ lineHeight: 0 }}
                              >
                                <FaCheckCircle
                                  className="text-success"
                                  style={{ fontSize: "1.5rem" }}
                                  title={t("CreditCardPage.markUsedTooltip")}
                                />
                              </div>
                            )}
                            <FaInfoCircle className="text-muted" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </ListGroup.Item>
                </ListGroup>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Benefit Detail Modal */}
      <Modal show={!!selectedBenefit} onHide={() => setSelectedBenefit(null)}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedBenefit?.config?.label}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBenefit && (
            <>
              <p>{selectedBenefit.config?.description}</p>
              <dl className="row">
                <dt className="col-sm-5">{t("CreditCardPage.benefitType")}</dt>
                <dd className="col-sm-7">{selectedBenefit.config?.benefitType}</dd>

                <dt className="col-sm-5">{t("CreditCardPage.amount")}</dt>
                <dd className="col-sm-7">${selectedBenefit.config?.amount}</dd>

                <dt className="col-sm-5">{t("CreditCardPage.expirationDate")}</dt>
                <dd className="col-sm-7">{new Date(selectedBenefit.periodEnd).toLocaleDateString()}</dd>

                <dt className="col-sm-5">{t("CreditCardPage.renewalType")}</dt>
                <dd className="col-sm-7 text-capitalize">
                  {selectedBenefit.config?.periodType?.replace(/_/g, " ").toLowerCase()}
                </dd>
              </dl>
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* Mobile Floating Action Button */}
      <Button
        variant="primary"
        className="d-md-none fixed-bottom end-0 mb-3 me-3 rounded-circle shadow-lg"
        style={{ width: "60px", height: "60px" }}
        onClick={() => setShowAddModal(true)}
      >
        <FaPlusCircle size={24} />
      </Button>
    </Container>
  );
}
